import { ChevronDown, Award, Mail } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { YouTubeFacade } from './YouTubeFacade';

const Hero = () => {
  const isMobile = useIsMobile();

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-48 overflow-hidden bg-gradient-to-br from-blue-800 to-blue-900">
      <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
      
      <div className="container-custom py-16 md:py-24 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="reveal-slide lg:max-w-xl mx-auto lg:mx-0 text-center lg:text-left" style={{ '--reveal-delay': 0 } as React.CSSProperties}>
            <div className="inline-block px-4 py-2 mb-6 rounded-full bg-blue-100 text-blue-800 font-semibold text-sm border border-blue-200">
               Colorado First Plumbing and Sewer LLC - Licensed Plumbing Contractor
            </div>
            

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-white">
              Denver Plumber with Upfront,<br />
              Flat-Rate Pricing
            </h1>

            <h2 className="text-3xl md:text-4xl font-bold leading-tight mb-6 text-white">
              Colorado's <span className="text-gradient bg-gradient-to-r from-yellow-300 to-orange-400 bg-clip-text text-transparent">ONLY</span> Plumber with<br />
              Real Prices Online — No Hidden Fees
            </h2>
           
            <h3 className="text-xl mb-2 text-blue-200 max-w-xl font-semibold">
              No Hidden Fees. No Sales Games. Just Honest, Affordable, Upfront Pricing.
            </h3>
            <p className="text-lg mb-8 text-blue-100 max-w-xl">
              Serving All of Denver Metro, Including Castle Rock and Longmont
            </p>
            
            {/* Closure Notice — replaces former Call Now / Book Online CTAs */}
            <div className="bg-white/10 backdrop-blur-sm border-2 border-red-300/50 rounded-2xl p-6 mb-2 max-w-xl">
              <div className="text-red-200 font-bold uppercase tracking-wide text-sm mb-2">
                We&rsquo;re No Longer Taking Service Calls
              </div>
              <p className="text-white text-lg font-semibold leading-snug mb-3">
                Colorado First Plumbing and Sewer LLC permanently closed in March 2026.
              </p>
              <p className="text-blue-100 text-base leading-relaxed mb-4">
                After 8 years, 18,000+ jobs, and the trust of thousands of Colorado families &mdash; thank you. It has been our honor to serve you.
              </p>
              <a
                href="mailto:contact@bestmasterplumber.com"
                className="inline-flex items-center text-white hover:text-yellow-300 font-semibold transition-colors duration-200"
              >
                <Mail className="mr-2" size={18} />
                contact@bestmasterplumber.com
              </a>
            </div>
            
            {/* Trust Signals */}
            <div className="mt-8 flex flex-wrap justify-center lg:justify-start items-center gap-6 text-blue-100">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">1000+ Reviews Across All Platforms</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">360+ Google Reviews (4.8 Stars)</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">Master Plumber Operated & Family Owned</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">✓</span>
                </div>
                <span className="text-sm font-medium">18,000+ Jobs Completed</span>
              </div>
            </div>
          </div>
          
          <div className="relative reveal-slide z-0" style={{ '--reveal-delay': 3 } as React.CSSProperties}>
            <div className={`absolute ${isMobile ? '-top-12 -right-12' : '-top-16 -right-16'} z-30 ${isMobile ? 'w-40 h-40' : 'w-52 h-52'} pointer-events-none`}>
              <svg width="200" height="200" viewBox="0 0 250 250" className="w-full h-full">
                <path 
                  d="M125 10 L145 40 L170 15 L175 50 L210 35 L200 70 L240 75 L210 95 L245 120 L200 125 
                  L230 155 L185 155 L200 190 L160 170 L155 210 L125 175 L95 210 L90 170 L50 190 L65 155 
                  L20 155 L50 125 L5 120 L40 95 L10 75 L50 70 L40 35 L75 50 L80 15 L105 40 Z" 
                  fill="#0EA5E9"
                  stroke="#000000"
                  strokeWidth="5"
                  strokeLinejoin="round"
                  fillOpacity="0.98"
                />
              </svg>
              
              <div className="absolute inset-0 flex flex-col items-center justify-center -mt-4">
                <Award className={`${isMobile ? 'h-7 w-7' : 'h-10 w-10'} text-white mx-auto mb-1`} />
                <div className="text-center w-full">
                  <span className={`text-white font-bold ${isMobile ? 'text-sm' : 'text-base'} block leading-none`}>Licensed</span>
                  <span className={`text-white font-bold ${isMobile ? 'text-sm' : 'text-base'} block leading-none`}>Pro's</span>
                </div>
              </div>
            </div>
            
            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video max-w-md mx-auto">
              <YouTubeFacade
                videoId="WzQdOtVNeUU"
                title="Colorado First Plumbing Professional Service Video"
                autoplay={true}
                muted={true}
                loop={true}
                controls={false}
                className="absolute inset-0"
              />
            </div>
            
            {/* BirdEye Widget */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-md mx-auto mt-6 bg-white/10 backdrop-blur-sm p-4 border border-white/20">
              <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
              <div id="birdeye-fallback" style={{ display: 'none' }} className="text-blue-100 text-center p-4">
                <p className="font-semibold mb-2">⭐ 4.8 Stars • 300+ Reviews</p>
                <a 
                  href="/reviews" 
                  className="text-blue-200 hover:text-white underline"
                >
                  View Our Customer Reviews
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 scroll-down-indicator">
        <a href="#services" className="text-blue-200 hover:text-white transition-colors" aria-label="Scroll down to view services">
          <ChevronDown size={32} />
        </a>
      </div>
    </section>
  );
};

export default Hero;
